import { List, Map, OrderedMap, Record as ImmutableRecord, RecordOf, Set } from 'immutable';

import type { StakingError } from './saga/staking/types';
import type { FreeBet } from 'app/api/services/freebets/types';
import type { ObjectEntityNative } from 'app/lib/betting/betting-entities/reducers/objects/types';
import type { ReduxState } from 'app/redux/types';

import type { RecordOfBetEntity, RecordOfSportEntity, RecordOfStakeEntity } from 'app/lib/betting/betting-entities';
import { MatchType } from 'app/lib/betting/betting-entities';

import type { ResolveActions } from './actions/conflict';
import type { InsertInQueueForAdd } from './actions/entries';
import {
  AccountType,
  BetErrorCode,
  ConflictCase,
  FreebetPossibleStatus,
  MatchErrorCode,
  StakeErrorCode,
  SubmissionState,
  TypeOfError,
} from './constants';
import type { BigStakeDAO } from './types/backend/bigStakes';
import type { PledgeStakeDAO } from './types/backend/paymentOptions';

// ---

// Entity для BetEntry, StakeEntry, MatchEntry, SportEntry будет существовать всегда
// Поэтому, чтобы исключить бесполезные проверки в коде, сделал вот такой костыль для TS
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DefaultEntry: any = ImmutableRecord({}, 'DefaultEntry');

// ---

type BetIdType = number;

// ---

type ObjectEntryType = Omit<ObjectEntityNative, 'matchIds'>;

export type RecordOfObjectEntryType = RecordOf<ObjectEntryType>;

export const ObjectEntry = ImmutableRecord<ObjectEntryType>({
  id: 0,
  type: undefined,
  name: '',
  icon: '',
}, 'ObjectEntry');

// ---

type BetEntryType = {
  entity: RecordOfBetEntity;
  price: number;
};

export type RecordOfBetEntryType = RecordOf<BetEntryType>;

export const BetEntry = ImmutableRecord<BetEntryType>({
  entity: DefaultEntry(),
  price: 0,
}, 'BetEntry');

// ---

type StakeEntryType = {
  entity: RecordOfStakeEntity;
  betIds: List<BetIdType>;
};

export type RecordOfStakeEntryType = RecordOf<StakeEntryType>;

export const StakeEntry = ImmutableRecord<StakeEntryType>({
  entity: DefaultEntry(),
  betIds: List(),
}, 'StakeEntry');

// ---

export type MatchObjects = Map<number, RecordOfObjectEntryType>;

type MatchEntityType = {
  id: number;
  sportId: number;
  tournamentUnionId: number;
  tournamentId: number;
  tournamentStageId: number;
  name: string;
  isOpen: boolean;
  type: MatchType;
  slotType?: number;
  startDate?: Date;
  objects: MatchObjects;
};

export type RecordOfMatchEntityType = RecordOf<MatchEntityType>;

export const MatchEntity = ImmutableRecord<MatchEntityType>(
  {
    id: 0,
    sportId: 0,
    tournamentUnionId: 0,
    tournamentId: 0,
    tournamentStageId: 0,
    name: '',
    isOpen: true,
    type: MatchType.OUTRIGHT,
    slotType: undefined,
    startDate: undefined,
    objects: Map(),
  },
  'MatchEntity',
);

// ---

type MatchEntryType = {
  entity: RecordOfMatchEntityType;
  betIds: List<BetIdType>;
};

export type RecordOfMatchEntryType = RecordOf<MatchEntryType>;

export const MatchEntry = ImmutableRecord<MatchEntryType>({
  entity: MatchEntity(),
  betIds: List(),
}, 'MatchEntry');

// ---

type SportEntryType = {
  entity: RecordOfSportEntity;
  matchIds: List<number>;
};

export type RecordOfSportEntryType = RecordOf<SportEntryType>;

export const SportEntry = ImmutableRecord<SportEntryType>({
  entity: DefaultEntry(),
  matchIds: List(),
}, 'SportEntry');

// ---

export type EntryAmountSelector = (state: ReduxState) => RecordOfEntryAmountType;

type EntryAmountType = {
  selector: EntryAmountSelector | null;
  value: string;
  account: AccountType;
};

export type RecordOfEntryAmountType = RecordOf<EntryAmountType>;

export const EntryAmount = ImmutableRecord<EntryAmountType>({
  selector: null,
  value: '',
  account: AccountType.MAIN_ACCOUNT,
}, 'EntryAmount');

// ---

type StakeTypeAmountType = {
  value: string;
  numeric: number;
  account: AccountType;
};

export type RecordOfStakeTypeAmountType = RecordOf<StakeTypeAmountType>;

export const StakeTypeAmount = ImmutableRecord<StakeTypeAmountType>({
  value: '',
  numeric: NaN,
  account: AccountType.MAIN_ACCOUNT,
}, 'StakeTypeAmount');

// ---

type EntryLimitsType = {
  min: number;
  max: number;
};

export type RecordOfEntryLimitsType = RecordOf<EntryLimitsType>;

export const EntryLimits = ImmutableRecord<EntryLimitsType>({
  min: 0,
  max: Infinity,
}, 'EntryLimits');

// ---

type RankEntryType = {
  min: number;
  max: number;
  combinations: number;
};

export type RecordOfRankEntryType = RecordOf<RankEntryType>;

export const RankEntry = ImmutableRecord<RankEntryType>({
  min: 0,
  max: Infinity,
  combinations: 0,
}, 'RankEntry');

// ---

type PaymentOptionsEntry = {
  isPledgePossible: boolean;
  isFreeBetPossible: boolean;
  freeBetPossibleStatus: FreebetPossibleStatus;
};

export type RecordOfPaymentOptionsEntry = RecordOf<PaymentOptionsEntry>;

export const PaymentOptionsEntry = ImmutableRecord<PaymentOptionsEntry>({
  isPledgePossible: false,
  isFreeBetPossible: false,
  freeBetPossibleStatus: FreebetPossibleStatus.MATCH_FREEBET_IS_FORBIDDEN,
}, 'paymentOptionsEntry');

// ---

type EntryErrorType = {
  code: StakeErrorCode | MatchErrorCode | BetErrorCode | null;
  type: TypeOfError;
  limits: RecordOfEntryLimitsType | null;
  rejectReason: StakingError['rejectReason'] | null;
};

export type RecordOfEntryErrorType = RecordOf<EntryErrorType>;

export const EntryError = ImmutableRecord<EntryErrorType>({
  code: null,
  type: TypeOfError.GENERAL,
  limits: null,
  rejectReason: null,
}, 'EntryError');

// ---

type BetInfoType = {
  id: BetIdType;
  dialogId: string;
  matchId: number;
  amount: RecordOfEntryAmountType | null;
  submission: SubmissionState;
  limits: RecordOfEntryLimitsType;
  error: RecordOfEntryErrorType | null;
  paymentOptions: RecordOfPaymentOptionsEntry;
  pledgeStake: PledgeStakeDAO | null;
  freeBet: FreeBet | null;
};

export type RecordOfBetInfoType = RecordOf<BetInfoType>;

export const BetInfo = ImmutableRecord<BetInfoType>({
  id: 0,
  dialogId: '',
  matchId: 0,
  amount: null,
  submission: SubmissionState.AWAITING,
  limits: EntryLimits(),
  error: null,
  paymentOptions: PaymentOptionsEntry(),
  pledgeStake: null,
  freeBet: null,
}, 'betInfo');

// ---

type InsertInQueueForAddPayload = InsertInQueueForAdd['payload'];

type Conflict = {
  case: ConflictCase | null;
  items: OrderedMap<number, InsertInQueueForAddPayload>;
  highlightEntryIds: Set<number>;
  actions: List<ResolveActions>;
  lastTriggeredTime: number | null;
};

export type RecordOfConflict = RecordOf<Conflict>;

export const Conflict = ImmutableRecord<Conflict>({
  case: null,
  items: OrderedMap(),
  highlightEntryIds: Set(),
  actions: List(),
  lastTriggeredTime: null,
}, 'conflict');

// ---

type BigStake = {
  submission: SubmissionState;
} & BigStakeDAO;

export type RecordOfBigStake = RecordOf<BigStake>;

export const BigStake = ImmutableRecord<BigStake>({
  submission: SubmissionState.AWAITING,
  dialogId: '',
  name: '',
  description: '',
  matchNames: [],
  amount: 0,
  currency: 'RUB',
}, 'conflict');
